import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import MarkdownImage from './MarkdownImage';
import 'katex/dist/katex.min.css';

const ExplanationRenderer = ({ content }) => {
  if (!content) {
    console.log('ExplanationRenderer: No content provided');
    return <div className="text-gray-500 italic">No explanation available</div>;
  }

  console.log('ExplanationRenderer content:', content);

  // Custom components for markdown elements
  const components = {
    // Style headings
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold text-gray-800 mb-4 mt-6 first:mt-0 border-b-2 border-blue-200 pb-2">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-5 first:mt-0 border-b border-gray-200 pb-1">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold text-gray-800 mb-3 mt-4 first:mt-0">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-base font-semibold text-gray-700 mb-2 mt-3 first:mt-0">
        {children}
      </h4>
    ),
    // Style paragraphs
    p: ({ children }) => (
      <p className="mb-3 leading-relaxed text-gray-700">
        {children}
      </p>
    ),
    // Style lists
    ul: ({ children }) => (
      <ul className="list-disc list-inside mb-3 text-gray-700 space-y-1">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal list-inside mb-3 text-gray-700 space-y-1">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="ml-2">
        {children}
      </li>
    ),
    // Style strong/bold text
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-800">
        {children}
      </strong>
    ),
    // Style emphasis/italic text
    em: ({ children }) => (
      <em className="italic text-gray-700">
        {children}
      </em>
    ),
    // Style code
    code: ({ children, inline }) => (
      inline ? (
        <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm font-mono">
          {children}
        </code>
      ) : (
        <code className="block bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono whitespace-pre-wrap">
          {children}
        </code>
      )
    ),
    // Style tables
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full border-collapse border border-gray-300 bg-white rounded-lg shadow-sm">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-gray-50">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody className="divide-y divide-gray-200">
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="hover:bg-gray-50">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-800 bg-gray-50">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-gray-300 px-4 py-2 text-gray-700">
        {children}
      </td>
    ),
    // Style blockquotes
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-blue-500 bg-blue-50 pl-4 py-2 mb-3 italic text-gray-700">
        {children}
      </blockquote>
    ),
    // Style horizontal rules
    hr: () => (
      <hr className="my-6 border-t-2 border-gray-200" />
    ),
    // Style images with error handling
    img: (props) => <MarkdownImage {...props} />,
  };

  return (
    <div className="text-gray-700 prose-sm max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default ExplanationRenderer;
